import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Job, JobStatus, UpdateJobInput } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useClientStore } from '@core/client/useClientStore';
import SkillSetSelector from '@/components/job/create/SkillSetSelector';
import SingleDatePicker from '@/components/shared/SingleDatePicker';
import { pluralize } from '@core/util/utils';
import {
  toDayjsFromDateAndTime,
  getJobEndTimeString,
  getJobStartTimeString,
  buildDaysInputForJobUpdate,
} from '@/utils/utils';
import Button from '@/components/Button';
import TimeSelectors from '../job/create/TimeSelectors';
import FilledJobScheduleConfirmModal from './FilledJobScheduleConfirmModal';

/** Matches backend `CoreLoopConfigurationClass.initialConfiguration.minimumHoursForShift`. */
const defaultMinimumHoursForShift = 3;

const createJobEditValidationSchema = (minimumHoursForShift: number) => {
  const minimumDurationMessage = `Job duration must be at least ${minimumHoursForShift} ${pluralize('hour', minimumHoursForShift)}`;

  return Yup.object().shape({
    classification: Yup.object()
      .shape({
        id: Yup.string().required('Please select a job title'),
        name: Yup.string().required('Please select a job title'),
      })
      .required('Please select a job title'),
    startDate: Yup.string().required('Start date is required'),
    endDate: Yup.string().required('End date is required'),
    startTime: Yup.string().required('Start time is required'),
    endTime: Yup.string()
      .required('End time is required')
      .when(['startTime', 'startDate', 'endDate'], (_, schema) => {
        return schema
          .test('is-after-start', 'End must be after start', function (value) {
            const { startDate, endDate, startTime } = this.parent;
            if (!startDate || !endDate || !startTime || !value) return true;
            const start = toDayjsFromDateAndTime(startDate, startTime);
            const end = toDayjsFromDateAndTime(endDate, value);
            if (!start || !end) return true;
            return end.isAfter(start);
          })
          .test(
            'minimum-shift-duration',
            minimumDurationMessage,
            function (value) {
              const { startDate, endDate, startTime } = this.parent;
              if (!startDate || !endDate || !startTime || !value) return true;
              const start = toDayjsFromDateAndTime(startDate, startTime);
              const end = toDayjsFromDateAndTime(endDate, value);
              if (!start || !end) return true;
              const durationHours = end.diff(start, 'hour', true);
              return durationHours >= minimumHoursForShift;
            },
          );
      }),
  });
};

type Props = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  job: Job;
  onSuccess?: () => void;
};

const JobEditModal = ({
  isModalOpen,
  setIsModalOpen,
  job,
  onSuccess,
}: Props) => {
  const isFilledJob = job.itemStatus === JobStatus.Filled && !!job.userId;

  const { updateJob } = useJobStore();
  const { coreLoopConfigurationItems, ensureCoreLoopConfiguration } =
    useCoreLoopConfigurationStore();
  const {
    classificationGroups: tenantClassificationGroups,
    fetchClassificationGroups,
    getClassificationGroupsByClassifications,
    isLoading: isClassificationLoading,
  } = useClassificationStore();
  const { clients, getClientById, fetchClientById } = useClientStore();
  const [classificationSearch, setClassificationSearch] = useState('');
  const [pendingFilledSubmission, setPendingFilledSubmission] = useState<{
    input: UpdateJobInput;
  } | null>(null);
  const [isConfirmingFilledSubmit, setIsConfirmingFilledSubmit] =
    useState(false);

  const minimumHoursForShift =
    coreLoopConfigurationItems?.minimumHoursForShift ??
    defaultMinimumHoursForShift;

  useEffect(() => {
    if (!isModalOpen || !job.tenantId) return;
    ensureCoreLoopConfiguration(job.tenantId);
  }, [isModalOpen, job.tenantId, ensureCoreLoopConfiguration]);

  useEffect(() => {
    if (!isModalOpen || !job.tenantId) return;
    if (tenantClassificationGroups.length === 0) {
      fetchClassificationGroups(job.tenantId);
    }
  }, [
    isModalOpen,
    job.tenantId,
    tenantClassificationGroups.length,
    fetchClassificationGroups,
  ]);

  useEffect(() => {
    if (!isModalOpen || !job.clientId) return;
    const client = getClientById(job.clientId);
    if (!client) {
      fetchClientById(job.clientId);
    }
  }, [isModalOpen, job.clientId, getClientById, fetchClientById]);

  useEffect(() => {
    if (isModalOpen) {
      setClassificationSearch('');
    } else {
      setPendingFilledSubmission(null);
      setIsConfirmingFilledSubmit(false);
    }
  }, [isModalOpen]);

  const classificationGroups = useMemo(() => {
    const client = clients.find((c) => c.id === job.clientId);
    if (!client) return [];
    return getClassificationGroupsByClassifications(
      client.classifications || [],
    );
  }, [job.clientId, clients, getClassificationGroupsByClassifications]);

  const validationSchema = useMemo(
    () => createJobEditValidationSchema(minimumHoursForShift),
    [minimumHoursForShift],
  );

  const initialStartTime = getJobStartTimeString(job);
  const initialEndTime = getJobEndTimeString(job);

  const formik = useFormik({
    initialValues: {
      classification: job.classification
        ? {
            id: job.classification.id,
            name: job.classification.name ?? '',
            description: job.classification.description ?? undefined,
            classificationGroupId:
              job.classification.classificationGroupId ?? '',
          }
        : undefined,
      description: job.description || '',
      startDate: job.startDate
        ? dayjs(Number(job.startDate)).format('YYYY-MM-DD')
        : '',
      endDate: job.endDate
        ? dayjs(Number(job.endDate)).format('YYYY-MM-DD')
        : '',
      startTime: initialStartTime,
      endTime: initialEndTime,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      try {
        let startDateTime;
        if (values.startDate && values.startTime) {
          startDateTime = dayjs(
            `${values.startDate} ${values.startTime}`,
            'YYYY-MM-DD HH:mm',
            true,
          )
            .second(0)
            .millisecond(0);
        }

        let endDateTime;
        if (values.endDate && values.endTime) {
          endDateTime = dayjs(
            `${values.endDate} ${values.endTime}`,
            'YYYY-MM-DD HH:mm',
            true,
          )
            .second(0)
            .millisecond(0);
        } else if (values.startDate && values.endTime) {
          endDateTime = dayjs(
            `${values.startDate} ${values.endTime}`,
            'YYYY-MM-DD HH:mm',
            true,
          )
            .second(0)
            .millisecond(0);
        }

        if (!startDateTime || !endDateTime) {
          setStatus('Start and end date/time are required');
          return;
        }

        const startDate = String(startDateTime.valueOf());
        const endDate = String(endDateTime.valueOf());
        const days = buildDaysInputForJobUpdate(
          startDateTime,
          endDateTime,
          values.startTime,
          values.endTime,
        );

        if (isFilledJob) {
          setPendingFilledSubmission({
            input: {
              startDate,
              endDate,
              days,
              description: values.description || undefined,
            },
          });
          return;
        }

        const input: UpdateJobInput = {
          startDate,
          endDate,
          days,
          title: values.classification?.name ?? '',
          classificationId: values.classification?.id ?? '',
          description: values.description,
        };
        const updated = await updateJob(job.id, input);

        if (!updated) {
          toast.error('Failed to save changes. Please try again.');
          return;
        }
        setIsModalOpen(false);
        onSuccess?.();
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const submitPendingFilledChanges = async () => {
    if (!pendingFilledSubmission) return;

    setIsConfirmingFilledSubmit(true);

    try {
      const updated = await updateJob(job.id, pendingFilledSubmission.input);

      if (!updated) {
        toast.error('Failed to save changes. Please try again.');
        return;
      }

      setPendingFilledSubmission(null);
      setIsModalOpen(false);
      onSuccess?.();
      toast.success('Updates saved successfully');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'An unexpected error occurred',
      );
    } finally {
      setIsConfirmingFilledSubmit(false);
    }
  };

  const cancelFilledConfirmation = () => {
    setPendingFilledSubmission(null);
  };

  if (!isModalOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 sm:px-6">
        <div className="max-h-[110vh] w-full max-w-4xl overflow-visible rounded-lg bg-white p-8 shadow-xl">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-2xl font-bold text-gray-800">Edit Job</h3>
            <Button
              variant="text"
              onClick={() => setIsModalOpen(false)}
              className="min-w-0 px-2 py-2 font-normal"
            >
              <FontAwesomeIcon icon={faXmark} className="h-10 w-10" />
            </Button>
          </div>

          {isFilledJob && (
            <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">
                This job is already filled. You can only update its schedule
                (dates and times) and description. Title and classification are
                locked.
              </p>
            </div>
          )}

          {formik.status && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-600">{formik.status}</p>
            </div>
          )}

          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {!isFilledJob && (
              <SkillSetSelector
                value={formik.values.classification || {}}
                onChange={(value) =>
                  formik.setFieldValue('classification', value)
                }
                classificationGroups={classificationGroups}
                isLoading={isClassificationLoading}
                searchValue={classificationSearch}
                onSearchChange={setClassificationSearch}
                error={formik.errors.classification}
                touched={!!formik.touched.classification}
              />
            )}

            <div>
              <label
                className="mb-1 block text-sm font-medium text-gray-700"
                htmlFor="description"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                rows={4}
                className={`w-full rounded-md border bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500 ${
                  formik.touched.description && formik.errors.description
                    ? 'border-red-300'
                    : 'border-gray-300'
                }`}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="mt-1 text-sm text-red-600">
                  {formik.errors.description}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TimeSelectors
                time={formik.values.startTime || ''}
                onTimeChange={(val) => formik.setFieldValue('startTime', val)}
                timeError={formik.errors.startTime || ''}
                label="Start time"
                formikId="startTime"
                handleBlur={formik.handleBlur}
                touched={!!formik.touched.startTime}
              />

              <div>
                <label
                  className="mb-1 block text-sm font-medium text-gray-700"
                  htmlFor="startDate"
                >
                  Start date
                </label>
                <SingleDatePicker
                  value={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate).toDate()
                      : new Date()
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      'startDate',
                      dayjs(date).format('YYYY-MM-DD'),
                    );
                    formik.setFieldTouched('startDate', true, false);
                  }}
                  panelAlignment="left"
                  isInvalid={
                    !!(formik.touched.startDate && formik.errors.startDate)
                  }
                  className="w-full rounded-md px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                />
                {formik.touched.startDate && formik.errors.startDate && (
                  <div className="mt-1 text-sm text-red-600">
                    {formik.errors.startDate}
                  </div>
                )}
              </div>

              <TimeSelectors
                time={formik.values.endTime || ''}
                onTimeChange={(val) => formik.setFieldValue('endTime', val)}
                timeError={formik.errors.endTime || ''}
                label="End time"
                formikId="endTime"
                handleBlur={formik.handleBlur}
                touched={formik.dirty}
              />

              <div>
                <label
                  className="mb-1 block text-sm font-medium text-gray-700"
                  htmlFor="endDate"
                >
                  End date
                </label>
                <SingleDatePicker
                  value={
                    formik.values.endDate
                      ? dayjs(formik.values.endDate).toDate()
                      : new Date()
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      'endDate',
                      dayjs(date).format('YYYY-MM-DD'),
                    );
                    formik.setFieldTouched('endDate', true, false);
                  }}
                  panelAlignment="left"
                  isInvalid={
                    !!(formik.touched.endDate && formik.errors.endDate)
                  }
                  className="w-full rounded-md px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                />
                {formik.touched.endDate && formik.errors.endDate && (
                  <div className="mt-1 text-sm text-red-600">
                    {formik.errors.endDate}
                  </div>
                )}
              </div>
            </div>

            <div className="relative top-5 flex items-end justify-end gap-4 border-t pt-10">
              <Button
                variant="outline"
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="py-2 text-sm"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  formik.isSubmitting || !formik.dirty || !formik.isValid
                }
                className="py-2 text-sm"
              >
                {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <FilledJobScheduleConfirmModal
        isOpen={!!pendingFilledSubmission}
        onClose={cancelFilledConfirmation}
        onConfirm={submitPendingFilledChanges}
        startDate={pendingFilledSubmission?.input.startDate ?? null}
        endDate={pendingFilledSubmission?.input.endDate ?? null}
        isSubmitting={isConfirmingFilledSubmit}
      />
    </>
  );
};

export default JobEditModal;
